import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useSession } from "@/hooks/useSession";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { isLoading, isSignedIn } = useSession();
  const [params] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const oauthError = params.get("error");
    if (oauthError) {
      const description =
        params.get("error_description") ?? params.get("message") ?? oauthError;
      setErrorMessage(description);
    }
  }, [params]);

  useEffect(() => {
    if (errorMessage) return;
    if (isLoading) return;
    if (isSignedIn) {
      navigate("/dashboard", { replace: true });
    } else {
      // Better Auth landed here without a session — likely a cancelled
      // OAuth flow or a still-propagating cookie. Bounce back to /login
      // and let the user retry; the session atom will refetch on mount.
      navigate("/login", { replace: true });
    }
  }, [errorMessage, isLoading, isSignedIn, navigate]);

  if (errorMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-purple-950">
        <div
          role="alert"
          className="max-w-md w-full rounded-lg border bg-card p-6 text-center shadow"
        >
          <h1 className="text-lg font-semibold">Sign-in failed</h1>
          <p className="mt-2 text-sm text-muted-foreground">{errorMessage}</p>
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-4 text-sm text-primary underline-offset-4 hover:underline"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div
        className="flex items-center gap-3 text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Finishing sign-in&hellip;</span>
      </div>
    </div>
  );
}
